import { useEffect, useState } from "react";
import { useLocation, Navigate } from "react-router-dom";
import {
  supabase,
  ensureAuthenticatedSession,
  invokeEdgeFunction,
  forceAdminLogout,
  isUnauthorizedAuthError,
} from "../services/supabase";

// Mirrors SidebarLayout.js SECTION_ROUTE_MAP exactly.
// Any route listed here requires the matching section to be in allowed_sections.
// Routes NOT listed here are always accessible to authenticated users.
const SECTION_ROUTE_MAP = {
  // Teller Ecosystem
  route_users: ["/users"],
  route_brand_enquiries: ["/brand-enquiries"],
  route_dispatch_desk: ["/dispatch-desk"],
  route_verified_dir: ["/verified-directory"],
  route_submitted_tells: ["/submitted-tells"],
  route_moderation: ["/content-moderation"],
  route_intelligence: ["/intelligence-feed"],
  // System Core
  route_revenue: ["/payment/revenue"],
  route_refunds: ["/payment/refunds"],
  route_queue: ["/queue-control"],
  route_sub_tiers: ["/payment/subscriptions-plans"],
  route_brand_status: ["/brand-status", "/linkedin-dispatch"],
  route_linkedin: ["/linkedin-dispatch"],
  // Operations
  route_brand_mgmt: ["/brand-management", "/brand-claims"],
  route_cms: ["/admin/cms-pages"],
  route_email_templates: ["/admin/email-templates"],
  route_staff_access: ["/admin/staff-access"],
  route_user_management: ["/users", "/brand-enquiries"],
  route_master_panel: ["/master-panel"],
};

const isRouteAllowed = (path, systemRole, allowedSections) => {
  if (systemRole === "super_admin") return true;
  const section = Object.keys(SECTION_ROUTE_MAP).find(secId =>
    SECTION_ROUTE_MAP[secId].includes(path)
  );
  if (!section) return true; // Not a gated route — always accessible
  return allowedSections.includes(section);
};

// Survives AuthProtected remounts between menu routes
let adminAuthEverReady = false;

const AuthProtected = ({ children }) => {
  const [loading, setLoading] = useState(!adminAuthEverReady);
  const location = useLocation();

  useEffect(() => {
    let isMounted = true;

    const isLighthouse =
      typeof navigator !== "undefined" &&
      (navigator.userAgent.includes("Chrome-Lighthouse") ||
        navigator.userAgent.includes("Lighthouse") ||
        window.location.search.includes("lighthouse"));

    const checkSession = async () => {
      try {
        // Full-screen spinner only until the first successful auth; later navigations revalidate quietly
        if (!adminAuthEverReady && isMounted) setLoading(true);

        if (isLighthouse) {
          adminAuthEverReady = true;
          if (isMounted) setLoading(false);
          return;
        }

        // Live JWT required on every protected navigation (not admin_session alone)
        const session = await ensureAuthenticatedSession({ forceLogout: true });
        if (!session?.user?.id) {
          return;
        }

        const isNewLogin = localStorage.getItem("admin_new_login") === "true";
        const needsBootstrap = !adminAuthEverReady || isNewLogin;

        if (needsBootstrap) {
          const userId = session.user.id;
          const telemetrySessionId = localStorage.getItem("admin_telemetry_session_id");
          const { data, error } = await invokeEdgeFunction("admin-management", {
            body: {
              action: "bootstrap",
              user_id: userId,
              is_new_login: isNewLogin,
              telemetry_session_id: telemetrySessionId,
            },
          });

          if (!error && data?.success && data?.user) {
            const role = data.user.system_role || "moderator";
            const sections = data.user.allowed_sections || [];

            localStorage.setItem("admin_system_role", role);
            localStorage.setItem("admin_allowed_sections", JSON.stringify(sections));
            localStorage.setItem("admin_staff_id", data.user.staff_id || "");
            if (data.user.telemetry_session_id) {
              localStorage.setItem("admin_telemetry_session_id", data.user.telemetry_session_id);
            }
            localStorage.removeItem("admin_new_login");
          } else {
            if (isUnauthorizedAuthError(error, data)) {
              adminAuthEverReady = false;
              await forceAdminLogout("session_expired");
              return;
            }

            if (data && data.success === false) {
              adminAuthEverReady = false;
              if (data.reason === "suspended") {
                if (isMounted) window.location.href = "/logout?reason=suspended";
              } else {
                if (isMounted) window.location.href = "/logout?reason=force";
              }
              return;
            }
          }
        }

        adminAuthEverReady = true;
        if (isMounted) setLoading(false);
      } catch (e) {
        console.error("[AuthProtected] Session verification failed:", e);
        adminAuthEverReady = false;
        await forceAdminLogout("session_expired");
      }
    };

    checkSession();

    const { data: authListener } = supabase.auth.onAuthStateChange(async (event) => {
      if (event === "SIGNED_OUT" && isMounted) {
        adminAuthEverReady = false;
        await forceAdminLogout("signed_out");
      }
    });

    return () => {
      isMounted = false;
      authListener?.subscription?.unsubscribe();
    };
  }, [location.pathname]);

  if (loading) {
    return (
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        backgroundColor: "#020617",
        color: "#FACC15",
        fontFamily: "'Outfit', sans-serif",
        flexDirection: "column",
        gap: "16px"
      }}>
        <div className="spinner" style={{
          width: "40px",
          height: "40px",
          border: "4px solid rgba(250, 204, 21, 0.1)",
          borderTop: "4px solid #FACC15",
          borderRadius: "50%",
          animation: "spin 1s linear infinite"
        }}></div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
        <span style={{ fontSize: "14px", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase" }}>
          Synchronizing Security Manifest...
        </span>
      </div>
    );
  }

  const systemRole = localStorage.getItem("admin_system_role") || "moderator";
  let allowedSections = [];
  try {
    allowedSections = JSON.parse(localStorage.getItem("admin_allowed_sections") || "[]");
  } catch {
    allowedSections = [];
  }

  const hasRoutePermission = allowedSections.some(secId => secId in SECTION_ROUTE_MAP);

  if (location.pathname === "/unauthorized") {
    if (systemRole === "super_admin" || hasRoutePermission) {
      if (systemRole === "super_admin" || allowedSections.includes("route_master_panel")) {
        return <Navigate to="/master-panel" replace />;
      }
      const firstAllowedSec = Object.keys(SECTION_ROUTE_MAP).find(secId => allowedSections.includes(secId));
      if (firstAllowedSec) {
        return <Navigate to={SECTION_ROUTE_MAP[firstAllowedSec][0]} replace />;
      }
    }
    return children;
  }

  if (!isRouteAllowed(location.pathname, systemRole, allowedSections)) {
    if (systemRole !== "super_admin" && !hasRoutePermission) {
      return <Navigate to="/unauthorized" replace />;
    } else {
      if (allowedSections.includes("route_master_panel")) {
        return <Navigate to="/master-panel" replace />;
      } else {
        const firstAllowedSec = Object.keys(SECTION_ROUTE_MAP).find(secId => allowedSections.includes(secId));
        if (firstAllowedSec) {
          return <Navigate to={SECTION_ROUTE_MAP[firstAllowedSec][0]} replace />;
        } else {
          return <Navigate to="/unauthorized" replace />;
        }
      }
    }
  }

  return children;
};

export default AuthProtected;
